import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send, Repeat } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

export default function TradeProposalModal({ open, onClose, targetListing, preselectedIds = [] }) {
  const [myListings, setMyListings] = useState([]);
  const [underReview, setUnderReview] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const preselectedKey = (preselectedIds || []).join("|");

  useEffect(() => {
    if (!open) return;
    setMessage("");
    api.get("/auth/me").then(({ data }) => {
      return api.get(`/users/${data.user_id}/listings`).then(({ data }) => {
        const items = data.items || [];
        const active = items.filter(l => l.status === "active");
        setMyListings(active);
        setUnderReview(items.filter(l => l.status === "under_review"));
        // Pre-select any preselectedIds that map to an active listing (deep-link from digest email)
        const activeIds = new Set(active.map(l => l.id));
        const validPreselect = (preselectedKey ? preselectedKey.split("|") : []).filter(id => activeIds.has(id));
        setSelected(validPreselect);
      });
    }).catch(() => { setMyListings([]); setUnderReview([]); setSelected([]); });
  }, [open, preselectedKey]);

  if (!open) return null;

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const submit = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least one item to offer");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/trade-offers", {
        listing_id: targetListing.id,
        offered_listing_ids: selected,
        message,
      });
      track("send_offer", { source: "listing", offered_count: selected.length, target_count: 1 });
      toast.success("Trade proposal sent!");
      onClose();
      navigate(`/messages/${data.conversation_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send proposal");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-0 md:p-6" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white w-full md:max-w-2xl md:rounded-3xl rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="proposal-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#E2E2E2]">
          <div>
            <h3 className="font-display text-lg font-semibold">Propose a trade</h3>
            <p className="text-xs text-zinc-500">For {targetListing?.title}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F1F5F9]" data-testid="proposal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          {/* Target preview */}
          <div className="vyrti-card !shadow-none border border-[#E2E2E2] p-3 flex items-center gap-3">
            {targetListing?.images?.[0] && (
              <img src={imgSrc(targetListing.images[0])} alt="" className="w-16 h-16 rounded-xl object-cover" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-zinc-500">You want</p>
              <p className="font-medium truncate">{targetListing?.title}</p>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div className="w-9 h-9 rounded-full bg-[#E0EDFA] text-[#0654BA] flex items-center justify-center">
              <Repeat className="w-4 h-4" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium flex items-center gap-1.5">
                <Repeat className="w-4 h-4 text-[#0654BA]" />
                {selected.length > 1 ? `Bundling ${selected.length} items` : "Pick what you'd like to offer"}
              </p>
              <span className="vyrti-pill !text-[11px] !py-0.5">{selected.length} selected</span>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3">Pick one item, or bundle several to balance the trade.</p>
            {myListings.length === 0 ? (
              underReview.length > 0 ? (
                <div className="vyrti-card p-5 !shadow-none border border-amber-200 bg-amber-50" data-testid="proposal-under-review-note">
                  <p className="text-sm font-medium text-amber-900">Your {underReview.length === 1 ? "listing is" : "listings are"} still under review</p>
                  <p className="text-xs text-amber-800 mt-1.5">
                    {underReview.map(l => `"${l.title}"`).join(", ")} can't be offered until our team approves {underReview.length === 1 ? "it" : "them"}. Reviews usually happen quickly — check back soon.
                  </p>
                </div>
              ) : (
                <div className="vyrti-card p-6 text-center !shadow-none border border-dashed border-zinc-200">
                  <p className="text-sm text-zinc-600">You don't have any active listings to offer.</p>
                  <button
                    onClick={() => { onClose(); navigate("/post"); }}
                    className="btn-pill btn-accent mt-3"
                    data-testid="proposal-cta-post"
                  >Post your first listing</button>
                </div>
              )
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 max-h-72 overflow-y-auto pr-1">
                  {myListings.map(l => {
                    const active = selected.includes(l.id);
                    return (
                      <button key={l.id} onClick={() => toggle(l.id)} className={`text-left rounded-2xl border-2 transition-all overflow-hidden ${active ? "border-[#0654BA] shadow-[0_8px_24px_rgba(59,130,246,0.18)]" : "border-[#E2E2E2] hover:border-zinc-300"}`} data-testid={`proposal-offer-${l.id}`}>
                        <div className="aspect-[4/3] bg-[#E2E2E2] relative">
                          {l.images?.[0] && <img src={imgSrc(l.images[0])} alt={l.title} className="w-full h-full object-cover" />}
                          {active && (
                            <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#0654BA] text-white text-xs flex items-center justify-center font-semibold">
                              {selected.indexOf(l.id) + 1}
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-sm font-medium line-clamp-1">{l.title}</p>
                          <p className="text-[11px] text-zinc-500 line-clamp-1">{l.category}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {underReview.length > 0 && (
                  <p className="text-[11px] text-amber-700 mt-2" data-testid="proposal-review-hint">
                    {underReview.length} of your listings {underReview.length === 1 ? "is" : "are"} under review and can't be offered yet.
                  </p>
                )}
              </>
            )}
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Add a message (optional)</p>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              className="input-text min-h-[80px] resize-y"
              placeholder="Hey! I think this would be a great trade because…"
              data-testid="proposal-message"
            />
          </div>
        </div>

        <div className="p-5 border-t border-[#E2E2E2] flex items-center justify-between">
          <p className="text-xs text-zinc-500">{selected.length} item{selected.length === 1 ? "" : "s"} selected</p>
          <button
            onClick={submit}
            disabled={submitting || selected.length === 0}
            className="btn-pill btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="proposal-submit"
          >
            <Send className="w-4 h-4" />
            {submitting ? "Sending…" : "Send proposal"}
          </button>
        </div>
      </div>
    </div>
  );
}
